import React, { useRef, useEffect, useState, useContext } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Chart, ArcElement, Tooltip, Legend, PieController } from 'chart.js';
import { ThemeContext } from '../contexts/ThemeContext';

// Register Chart.js components
Chart.register(ArcElement, Tooltip, Legend, PieController);

/**
 * Unified PieChartModal that can display either tickers or portfolios data
 * @param {Object} props Component props
 * @param {boolean} props.show Controls modal visibility
 * @param {Function} props.onHide Function to close the modal
 * @param {Array} [props.tickers] Array of ticker objects with symbol and current_value properties
 * @param {Array} [props.portfolios] Array of portfolio objects with name and balance properties
 * @param {number} [props.cash=0] Cash amount to display
 * @param {string} [props.title='Asset Distribution'] Chart title
 */
function PieChartModal({
  show,
  onHide,
  tickers = null,
  portfolios = null,
  cash = 0,
  title = 'Asset Distribution'
}) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [hasDrawnChart, setHasDrawnChart] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { effectiveTheme } = useContext(ThemeContext);
  const isDarkMode = effectiveTheme === 'dark';
  
  // Reset chart drawn state when modal is closed
  useEffect(() => {
    if (!show) {
      setHasDrawnChart(false);
      setSelectedCategory(null);
    }
  }, [show]);

  // Clean up chart instance when component unmounts
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  // Go back to main pie chart view
  const handleBackToMain = () => {
    setSelectedCategory(null);
  };

  // Create or update chart when modal is shown or data changes
  useEffect(() => {
    // Only draw chart if:
    // 1. Modal is shown
    // 2. Chart ref exists
    // 3. Either chart hasn't been drawn yet OR chart instance doesn't exist OR selectedCategory changed
    if (!show || !chartRef.current) return;

    // Destroy previous chart instance if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Prepare data
    const labels = [];
    const values = [];
    const backgroundColor = [];
    const dataItems = [];
    
    // Object to store category details including tickers
    const categoryDetails = {};
    
    // Colors palette for data items
    const colorPalette = [
      '#ff6384', '#ff9f40', '#ffcd56', '#4bc0c0', 
      '#9966ff', '#c9cbcf', '#8c9eff', '#69f0ae',
      '#ffa726', '#ef5350', '#7e57c2', '#29b6f6'
    ];
    
    // Check if we're in drill-down view or main view
    if (selectedCategory && tickers) {
      // Drill-down view - show tickers for the selected category
      tickers.forEach((ticker, index) => {
        if (ticker && ticker.current_value > 0) {
          // Check if ticker belongs to the selected category
          const categoryName = ticker.category?.name || 'Uncategorized';
          
          if (categoryName === selectedCategory) {
            labels.push(ticker.symbol);
            values.push(ticker.current_value);
            backgroundColor.push(colorPalette[index % colorPalette.length]);
            dataItems.push({
              label: ticker.symbol,
              value: ticker.current_value,
              ticker: ticker
            });
          }
        }
      });
    } else {
      // Main view - group by categories
      
      // Add cash to main view if applicable
      if (cash > 0 && !selectedCategory) {
        labels.push('Cash');
        values.push(cash);
        backgroundColor.push('#36a2eb'); // Blue for cash
        dataItems.push({
          label: 'Cash',
          value: cash
        });
      }
      
      // Group tickers by category
      if (tickers && tickers.length > 0) {
        // Create a map to store category totals
        const categoryMap = new Map();
        
        // Initialize category details with empty arrays for tickers
        categoryDetails['Uncategorized'] = {
          tickers: [],
          value: 0
        };
        
        // Group tickers by category and sum their values
        tickers.forEach(ticker => {
          if (ticker && ticker.current_value) {
            // Check if ticker has a category
            if (ticker.category && ticker.category.name) {
              const categoryName = ticker.category.name;
              
              // Initialize category if it doesn't exist yet
              if (!categoryDetails[categoryName]) {
                categoryDetails[categoryName] = {
                  tickers: [],
                  value: 0
                };
              }
              
              // Add ticker to category details
              categoryDetails[categoryName].tickers.push(ticker);
              categoryDetails[categoryName].value += ticker.current_value;
              
              if (categoryMap.has(categoryName)) {
                categoryMap.set(categoryName, categoryMap.get(categoryName) + ticker.current_value);
              } else {
                categoryMap.set(categoryName, ticker.current_value);
              }
            } else {
              // Add to uncategorized
              categoryDetails['Uncategorized'].tickers.push(ticker);
              categoryDetails['Uncategorized'].value += ticker.current_value;
              
              if (categoryMap.has('Uncategorized')) {
                categoryMap.set('Uncategorized', categoryMap.get('Uncategorized') + ticker.current_value);
              } else {
                categoryMap.set('Uncategorized', ticker.current_value);
              }
            }
          }
        });
        
        // Add categories to the chart data
        let colorIndex = 0;
        categoryMap.forEach((value, name) => {
          if (value > 0) {
            labels.push(name);
            values.push(value);
            backgroundColor.push(colorPalette[colorIndex % colorPalette.length]);
            dataItems.push({
              label: name,
              value: value,
              category: name,
              tickers: categoryDetails[name].tickers
            });
            colorIndex++;
          }
        });
      }
      
      // Add portfolio/account data if available
      if (portfolios && portfolios.length > 0) {
        portfolios.forEach((item, index) => {
          if (item && item.balance > 0) {
            // If we have a portfolioInfo property, use it to add context to the name
            const itemName = item.portfolioInfo 
              ? `${item.name} (${item.portfolioInfo})` 
              : item.name;
              
            labels.push(itemName);
            values.push(item.balance);
            backgroundColor.push(colorPalette[index % colorPalette.length]);
            dataItems.push({
              label: itemName,
              value: item.balance,
              portfolio: item
            });
          }
        });
      }
    }

    // Create new chart instance
    try {
      const ctx = chartRef.current.getContext('2d');
      
      // Custom plugin to draw labels on pie slices
      const labelPlugin = {
        id: 'pieLabels',
        afterDraw: (chart) => {
          const { ctx, data } = chart;
          ctx.save();
          
          // Calculate total value for percentage calculation
          const total = data.datasets[0].data.reduce((sum, value) => sum + value, 0);
          
          // Draw only percentage labels on each slice
          const meta = chart.getDatasetMeta(0);
          meta.data.forEach((element, index) => {
            const value = data.datasets[0].data[index];
            const percentage = ((value / total) * 100).toFixed(1);
            
            // Only draw labels for segments with enough space (>= 5% of total)
            if (percentage >= 5) {
              // Get the center point of the chart
              const centerX = chart.chartArea.width / 2 + chart.chartArea.left;
              const centerY = chart.chartArea.height / 2 + chart.chartArea.top;
              
              // Calculate the midpoint angle of the slice
              const midAngle = element.startAngle + (element.endAngle - element.startAngle) / 2;
              
              // Calculate the distance from center (position in the middle of the slice radius)
              const radius = element.outerRadius / 2;
              
              // Calculate the label position
              const labelX = centerX + radius * Math.cos(midAngle);
              const labelY = centerY + radius * Math.sin(midAngle);
              
              // Set text formatting
              ctx.font = 'bold 14px Arial';
              ctx.fillStyle = 'white';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              
              // Add text shadow for better visibility
              ctx.shadowColor = 'rgba(0, 0, 0, 0.75)';
              ctx.shadowBlur = 4;
              ctx.shadowOffsetX = 0;
              ctx.shadowOffsetY = 0;
              
              // Get display name and truncate if needed
              let displayName = data.labels[index];
              if (displayName && displayName.length > 10) {
                displayName = displayName.substring(0, 8) + '...';
              }
              
              // Display name above percentage
              ctx.fillText(displayName, labelX, labelY - 10);
              
              // Display percentage below
              ctx.fillText(`${percentage}%`, labelX, labelY + 10);
              
              // Reset shadow
              ctx.shadowColor = 'transparent';
              ctx.shadowBlur = 0;
            }
          });
          
          ctx.restore();
        }
      };
      
      chartInstance.current = new Chart(ctx, {
        type: 'pie',
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor,
              borderWidth: 1,
              borderColor: isDarkMode ? 'rgba(18, 18, 18, 0.8)' : '#fff',
              // Ensure no default labels
              datalabels: {
                display: false
              }
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          aspectRatio: 1, // Make the chart square
          plugins: {
            legend: {
              display: false // Hide the legend
            },
            // Disable any built-in datalabels plugin
            datalabels: {
              display: false,
              formatter: () => null,
              font: { size: 0 }
            },
            tooltip: {
              backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(0, 0, 0, 0.8)',
              titleColor: '#fff',
              bodyColor: '#fff',
              borderColor: isDarkMode ? 'rgba(51, 51, 51, 0.8)' : '#999',
              borderWidth: 1,
              callbacks: {
                // Enhanced tooltip that shows category, percentage, and tickers
                label: (context) => {
                  const value = context.raw || 0;
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                  const label = context.label || '';

                  // Start with the basic info
                  const tooltipLines = [`${label}: ${percentage}%`];

                  // Add tickers if this is a category with tickers
                  if (categoryDetails[label] && categoryDetails[label].tickers.length > 0) {
                    const tickersList = categoryDetails[label].tickers.map(t => t.symbol).join(', ');
                    tooltipLines.push(`Tickers: ${tickersList}`);
                  }

                  return tooltipLines;
                }
              }
            }
          },
          // Increase the chart size to use full modal space without legend
          layout: {
            padding: 30
          },
          onClick: (event, elements) => {
            if (elements && elements.length > 0) {
              const clickedIndex = elements[0].index;
              const clickedItem = dataItems[clickedIndex];
              
              // Only allow drill-down on categories in the main view
              if (!selectedCategory && clickedItem && clickedItem.category) {
                console.log('Drilling down to category:', clickedItem.category);
                setSelectedCategory(clickedItem.category);
              }
            }
          }
        },
        plugins: [labelPlugin],
      });
      
      // Mark that we've drawn the chart for this session
      setHasDrawnChart(true);
      
    } catch (error) {
      console.error("Error creating chart:", error);
    }
  }, [show, tickers, portfolios, cash, hasDrawnChart, selectedCategory, isDarkMode]);

  // Handle custom onHide to clean up chart when closing
  const handleHide = () => {
    if (onHide) {
      onHide();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleHide} 
      centered
      backdrop={true}
      keyboard={true}
      size="xl"
      dialogClassName="pie-chart-modal"
    >
      <Modal.Header closeButton>
        {selectedCategory && (
          <div className="d-flex align-items-center">
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={handleBackToMain}
              className="me-2"
            >
              <i className="fas fa-arrow-left"></i> Back
            </Button>
            <h5 className="mb-0">{selectedCategory} Composition</h5>
          </div>
        )}
        {!selectedCategory && (
          <h5 className="mb-0">{title}</h5>
        )}
      </Modal.Header>
      <Modal.Body className="p-0" style={{ backgroundColor: 'var(--card-bg)' }}>
        <div className="square-container" style={{
          width: '100%',
          height: '0',
          paddingBottom: '100%',
          position: 'relative',
          backgroundColor: 'var(--card-bg)'
        }}>
          <canvas 
            ref={chartRef} 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer'
            }}
          />
        </div>
      </Modal.Body>
      <style jsx="true">{`
        .pie-chart-modal {
          max-width: 80vh !important;
          width: 80vh;
          margin: 0 auto;
        }
        @media (max-width: 992px) {
          .pie-chart-modal {
            max-width: 95vw !important;
            width: 95vw;
          }
        }
      `}</style>
    </Modal>
  );
}

export default PieChartModal; 